import React, { useRef, useEffect, useCallback } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { DragItemTypes, type QueryDragItem } from './dnd-types';
import type { CollectionQuery } from '@/services/api';
import { useQueryLibrarySelectionStore } from '@/stores/queryLibrarySelectionStore';


interface DraggableQueryProps {
  query: CollectionQuery;
  collectionId: string;
  folderId?: string;
  isSelected?: boolean;
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  allQueryIds?: string[];
}

export const DraggableQuery: React.FC<DraggableQueryProps> = ({
  query,
  collectionId,
  folderId,
  isSelected,
  onClick,
  onContextMenu,
  style,
  children,
  allQueryIds = []
}) => {
  // const { createPreview, removePreview } = useDragPreview();
  const { 
    toggleQuerySelection, 
    selectRange, 
    isQuerySelected, 
    lastSelectedQueryId,
    getSelectedQueryIds 
  } = useQueryLibrarySelectionStore();
  
  const queryId = query.id || '';
  const isMultiSelected = isQuerySelected(queryId);
  
  const dragRef = useRef<HTMLDivElement>(null)
  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: DragItemTypes.QUERY,
    item: (): QueryDragItem => {
      // If multiple queries are selected, we'll handle batch operations later
      const selectedIds = getSelectedQueryIds();
      const isDraggingMultiple = selectedIds.length > 1 && isMultiSelected;
      
      const dragItem = {
        type: DragItemTypes.QUERY,
        id: query.id || '0',
        name: query.name || 'Untitled Query',
        collectionId,
        folderId,
        orderIndex: query.orderIndex,
        // Add metadata for multi-select (for future batch operations)
        selectedIds: isDraggingMultiple ? selectedIds : undefined,
        count: isDraggingMultiple ? selectedIds.length : 1
      };
      console.log('🚀 DRAG START:', dragItem);
      return dragItem;
    },
    collect: (monitor) => {
      const isDragging = monitor.isDragging();
      if (isDragging) {
        console.log('📊 DRAGGING:', query.name);
      }
      return { isDragging };
    },
    end: (item, monitor) => {
      console.log('🏁 DRAG END:', { 
        item, 
        didDrop: monitor.didDrop(),
        dropResult: monitor.getDropResult()
      });
    }
  }), [query.id, query.name, collectionId, folderId, query.orderIndex, getSelectedQueryIds, isMultiSelected]);

  // Use empty image as drag preview to show custom preview
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
    drag(dragRef);
  }, [preview, drag]);
  
  const handleClick = (e: React.MouseEvent) => {
    const isCtrlPressed = e.ctrlKey || e.metaKey; // metaKey for Mac
    const isShiftPressed = e.shiftKey;
    
    console.log('🖱️ CLICK on draggable query', { 
      queryId, 
      isCtrlPressed, 
      isShiftPressed,
      lastSelectedQueryId,
      allQueryIds: allQueryIds.length,
      allQueryIdsContent: allQueryIds
    });
    
    if (isShiftPressed && lastSelectedQueryId && allQueryIds.length > 0) {
      // Handle shift selection
      console.log('📋 Attempting range selection', {
        from: lastSelectedQueryId,
        to: queryId,
        allQueryIds,
        fromIndex: allQueryIds.indexOf(lastSelectedQueryId),
        toIndex: allQueryIds.indexOf(queryId)
      });
      selectRange(lastSelectedQueryId, queryId, allQueryIds);
    } else {
      // Handle regular or ctrl selection
      toggleQuerySelection(queryId, isCtrlPressed, isShiftPressed);
    }
    
    // Call the original onClick handler
    onClick();
  };
  
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    // If this query is part of a multi-selection, keep the selection
    // Otherwise, select only this query
    if (!isMultiSelected) {
      toggleQuerySelection(queryId, false, false);
    }
    
    // Call the original context menu handler
    onContextMenu(e);
  }, [isMultiSelected, toggleQuerySelection, queryId, onContextMenu]);

  const handleMouseDown = useCallback(() => {
    console.log('🖱️ MOUSE DOWN on draggable query');
  }, []);

  return (
    <div
      ref={dragRef}
      className={`flex items-center py-1.5 px-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-grab active:cursor-grabbing group transition-colors ${
        isSelected || isMultiSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500' : ''
      } ${isDragging ? 'opacity-50 scale-95 shadow-lg' : ''} ${isDragging ? 'z-50' : ''}`}
      style={style}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onMouseDown={handleMouseDown}
      title={`${query.name} - Drag to move to another collection or folder`}
    >
      <DocumentTextIcon className="w-3.5 h-3.5 text-gray-400 mr-2 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center">
          <span className="text-xs text-gray-900 dark:text-gray-100 truncate">
            {query.name}
          </span>
          {query.tags && query.tags.length > 0 && (
            <div className="flex ml-2 space-x-1">
              {query.tags.slice(0, 2).map((tag, index) => (
                <span
                  key={index}
                  className="inline-block px-1.5 py-0.5 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                >
                  {tag}
                </span>
              ))}
              {query.tags.length > 2 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  +{query.tags.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
        {query.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
            {query.description}
          </p>
        )}
      </div>
      {children}
    </div>
  );
};